export const MS_PER_DAY = 86400000;
const MS_PER_HOUR = 3600000;
const MS_PER_MINUTE = 60000;

export const isValidDate = (date: Date) =>
  date instanceof Date && !Number.isNaN(date.getTime());

export const getI18nDateString = (
  date: Date,
  locale = "en-US",
  timeZone?: string,
) => {
  return new Date(date).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: timeZone || "UTC",
  });
};

export const isDateInPast = (end: Date) => new Date(end) < new Date();

export const getI18nDayMonthString = (date: Date, locale = "en-US") => {
  return new Date(date).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
};

export const getDaysAfterDate = (date: Date, int = 1) => {
  const dateObj = new Date(date);
  const daysAfter = new Date(dateObj.toISOString());
  daysAfter.setUTCDate(dateObj.getUTCDate() + int);
  return daysAfter;
};

export const getMonthsAfterDate = (date: Date, int = 1) => {
  const dateObj = new Date(date);
  const monthsAfter = new Date(dateObj.toISOString());
  monthsAfter.setUTCMonth(dateObj.getUTCMonth() + int);
  return monthsAfter;
};

export const getYearsAfterDate = (date: Date, int = 1) => {
  const dateObj = new Date(date);
  const yearsAfter = new Date(dateObj.toISOString());
  yearsAfter.setUTCFullYear(dateObj.getUTCFullYear() + int);
  return yearsAfter;
};

export const getDateDayMonth = (date: Date) => {
  const currentYear = new Date().getUTCFullYear();
  const stringDate = `${date.toLocaleString("en-US", { month: "short" })} ${date.getDate()}`;

  if (date.getUTCFullYear() > currentYear) {
    return `${stringDate}`;
  }
  return stringDate;
};

// returns a boolean if given date ends within this year
export const isEndingWithinThisYear = (date: Date) =>
  new Date(date).getUTCFullYear() <
  getYearsAfterDate(new Date(), 1).getUTCFullYear();

export function isDateInCurrentYear(date: Date): boolean {
  const currentYear = new Date().getUTCFullYear();
  const inputYear = date.getUTCFullYear();
  return inputYear === currentYear;
}

export const getI18nDateContextualString = (
  date: Date,
  locale = "en-US",
  timeZone?: string,
) => {
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    timeZone: timeZone || "UTC",
  };
  const inCurrentYear = isDateInCurrentYear(date);
  return new Date(date).toLocaleDateString(locale, {
    ...dateOptions,
    ...(!inCurrentYear ? { year: "numeric" } : {}),
  });
};

/**
 * Check how many days from today until a target date. Returns a number representing the number of days, or null if
 * given an invalid date.
 *
 * @param {Date} targetDate
 */
export function daysUntilTargetDate(targetDate: Date) {
  if (targetDate instanceof Date) {
    const today = new Date(Date.now());
    const todayUtc = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    );
    const targetUtc = Date.UTC(
      targetDate.getUTCFullYear(),
      targetDate.getUTCMonth(),
      targetDate.getUTCDate(),
    );
    const days = Math.floor((targetUtc - todayUtc) / MS_PER_DAY);
    return Number.isNaN(days) ? null : days;
  }
  return null;
}

/**
 * Calculate the time difference between now and a target date, returning days, hours, and minutes.
 * Returns null if given an invalid date.
 *
 * @param {Date} targetDate The target date to calculate time until
 * @returns {{ days: number; hours: number; minutes: number } | null} Object containing days, hours, and minutes until target date, or null if invalid
 */
export function getTimeUntilTargetDate(targetDate: Date) {
  if (!(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) {
    return null;
  }

  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();

  if (diff <= 0) {
    return null;
  }

  const days = Math.floor(diff / MS_PER_DAY);
  const hours = Math.floor((diff % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((diff % MS_PER_HOUR) / MS_PER_MINUTE);

  return { days, hours, minutes };
}
